import { memo, useState } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, Sparkles, Play } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { VideoDialog } from "@/components/VideoDialog";
import { FeatureMiniMockup } from "@/components/landing/FeatureMiniMockup";

const Hero = () => {
  const [videoOpen, setVideoOpen] = useState(false);
  const { t } = useTranslation();

  return (
    <section className="relative min-h-[calc(100vh-4rem)] flex items-center justify-center overflow-hidden pt-24 pb-12 bg-gradient-hero">
      {/* Animated mesh gradient background */}
      <div className="absolute inset-0 bg-gradient-mesh opacity-50" />
      
      {/* Animated gradient orbs — blur reduzido em mobile p/ fluidez */}
      <div className="absolute top-1/4 -left-32 w-[280px] h-[280px] md:w-[400px] md:h-[400px] bg-primary/30 rounded-full blur-[80px] md:blur-[120px] animate-pulse" />
      <div className="absolute bottom-1/4 -right-32 w-[240px] h-[240px] md:w-[350px] md:h-[350px] bg-accent/25 rounded-full blur-[70px] md:blur-[100px] animate-pulse" style={{ animationDelay: '1s' }} />
      <div className="hidden md:block absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] bg-secondary/20 rounded-full blur-[90px] animate-pulse" style={{ animationDelay: '2s' }} />

      {/* Content */}
      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <div className="max-w-4xl mx-auto text-center space-y-6 animate-fade-in">
          {/* Eyebrow editorial */}
          <div className="eyebrow">
            <Sparkles className="w-3 h-3 text-primary" />
            <span>{t('hero.badge')}</span>
          </div>

          {/* Heading — editorial serif */}
          <h1 className="heading-hero px-4" style={{ willChange: 'contents' }}>
            <span className="block mb-2 text-foreground">
              {t('hero.title.line1')}
            </span>
            <span className="block bg-gradient-primary bg-clip-text text-transparent italic leading-[1.25] pb-2">
              {t('hero.title.line2')}
            </span>
          </h1>

          {/* Subheading */}
          <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto px-4 leading-relaxed">
            {t('hero.subtitle')}
          </p>

          {/* Video CTA + signature mini-preview */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 max-w-3xl mx-auto my-4 items-stretch">
            {/* Interactive Video Showcase */}
            <div
              onClick={() => setVideoOpen(true)}
              className="group relative cursor-pointer p-5 rounded-2xl bg-gradient-to-b from-primary/20 to-accent/10 border border-primary/30 hover:border-primary/60 transition-all duration-500 hover:scale-105 hover:shadow-[0_0_40px_rgba(var(--primary),0.3)]"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-accent/10 to-primary/10 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-500" />
              <div className="relative space-y-3 h-full flex flex-col justify-center">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary/20 backdrop-blur-xl border border-primary/40 mb-3 group-hover:bg-primary/30 transition-all duration-300">
                    <Play className="w-6 h-6 text-primary fill-primary group-hover:scale-110 transition-transform" />
                  </div>
                  <p className="text-lg font-bold bg-gradient-primary bg-clip-text text-transparent mb-1">
                    {t('hero.videoTitle')}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t('hero.videoSubtitle')}
                  </p>
                </div>
              </div>
            </div>

            {/* Signature live preview — desktop only */}
            <div className="hidden md:flex relative rounded-2xl bg-gradient-to-br from-card/40 to-card/10 border border-white/10 backdrop-blur-xl p-4 shadow-glass overflow-hidden flex-col gap-2">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-accent/5 pointer-events-none" />
              <span className="relative text-[10px] font-medium uppercase tracking-wider text-primary/80 text-left">
                Pré-visualização ao vivo
              </span>
              <div className="relative flex-1 grid grid-rows-2 gap-2 min-h-[140px]">
                <div className="relative overflow-hidden rounded-xl border border-white/10 bg-gradient-to-br from-primary/10 to-accent/10">
                  <FeatureMiniMockup variant="progress" size="inline" />
                </div>
                <div className="relative overflow-hidden rounded-xl border border-white/10 bg-gradient-to-br from-accent/10 to-secondary/10">
                  <FeatureMiniMockup variant="navigation" size="inline" />
                </div>
              </div>
            </div>
          </div>

          {/* CTA Buttons with consistent spacing */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-4">
            <Link to="/courses">
              <Button variant="hero" size="lg" className="group w-full sm:w-auto min-w-[180px]">
                {t('hero.browseCourses')}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <Link to="/about">
              <Button variant="outline" size="lg" className="w-full sm:w-auto min-w-[180px]">
                {t('hero.learnMore')}
              </Button>
            </Link>
          </div>

          {/* Stats Grid */}
          <div className="pt-8 max-w-3xl mx-auto px-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="text-center p-4 md:p-5 rounded-xl bg-card/30 backdrop-blur-xl border border-primary/20 shadow-glass hover:shadow-glass-hover hover:border-primary/40 transition-all duration-300">
                <div className="font-serif text-2xl md:text-3xl font-semibold tracking-tight bg-gradient-primary bg-clip-text text-transparent mb-1 break-words">{t('hero.stats.smart')}</div>
                <div className="text-xs md:text-sm text-muted-foreground break-words">{t('hero.stats.smartDesc')}</div>
              </div>
              <div className="text-center p-4 md:p-5 rounded-xl bg-card/30 backdrop-blur-xl border border-primary/20 shadow-glass hover:shadow-glass-hover hover:border-primary/40 transition-all duration-300">
                <div className="font-serif text-2xl md:text-3xl font-semibold tracking-tight bg-gradient-primary bg-clip-text text-transparent mb-1 break-words">{t('hero.stats.live')}</div>
                <div className="text-xs md:text-sm text-muted-foreground break-words">{t('hero.stats.liveDesc')}</div>
              </div>
              <div className="text-center p-4 md:p-5 rounded-xl bg-card/30 backdrop-blur-xl border border-primary/20 shadow-glass hover:shadow-glass-hover hover:border-primary/40 transition-all duration-300">
                <div className="font-serif text-2xl md:text-3xl font-semibold tracking-tight bg-gradient-primary bg-clip-text text-transparent mb-1 break-words">{t('hero.stats.pro')}</div>
                <div className="text-xs md:text-sm text-muted-foreground break-words">{t('hero.stats.proDesc')}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom gradient fade with proper positioning */}
      <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-background to-transparent pointer-events-none" />
      
      <VideoDialog 
        open={videoOpen} 
        onOpenChange={setVideoOpen}
        videoId="I64IuEE1x2I"
        title={t('hero.videoTitle')}
      />
    </section>
  );
};

export default memo(Hero);
